import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { CheckCircle, XCircle, Clock, Search, Filter, User, Calendar, FileText, Check, X } from 'lucide-react';

const ApprovalsPage = () => {
  const { user } = useAuth();
  const [approvals, setApprovals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('pending');

  useEffect(() => { fetchApprovals(); }, [filterStatus]);

  const fetchApprovals = async () => {
    try {
      setLoading(true);
      const params = filterStatus !== 'all' ? { status: filterStatus } : {};
      const response = await api.get('/approvals', { params });
      setApprovals(response.data);
    } catch (error) {
      toast.error('Eroare la incarcarea aprobarilor');
    } finally {
      setLoading(false);
    }
  };

  const handleDecision = async (approvalId, decision) => {
    try {
      await api.put(`/approvals/${approvalId}`, {
        status: decision,
        notes: ''
      });
      toast.success(decision === 'approved' ? 'Aprobat!' : 'Respins!');
      fetchApprovals();
    } catch (error) {
      toast.error('Eroare la procesarea cererii');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-700',
      approved: 'bg-green-100 text-green-700',
      rejected: 'bg-red-100 text-red-700'
    };
    const labels = {
      pending: 'In Asteptare',
      approved: 'Aprobat',
      rejected: 'Respins'
    };
    return <Badge className={styles[status] || 'bg-slate-100'}>{labels[status] || status}</Badge>;
  };

  const getTypeLabel = (type) => {
    const labels = {
      material_request: 'Cerere Materiale',
      cost_approval: 'Aprobare Cost',
      revision_completion: 'Finalizare Revizie',
      stock_transfer: 'Transfer Stoc'
    };
    return labels[type] || type;
  };

  const filtered = approvals.filter(a => 
    a.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    a.requested_by_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const isAdmin = user?.role === 'admin' || user?.role === 'manager';

  const stats = {
    pending: approvals.filter(a => a.status === 'pending').length,
    approved: approvals.filter(a => a.status === 'approved').length,
    rejected: approvals.filter(a => a.status === 'rejected').length
  };

  return (
    <div className="space-y-6" data-testid="approvals-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Aprobari</h1>
          <p className="text-slate-600 mt-1">Gestioneaza cererile de aprobare</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <Card 
          className={`card-industrial cursor-pointer ${filterStatus === 'pending' ? 'border-yellow-500' : ''}`}
          onClick={() => setFilterStatus('pending')}
        >
          <CardContent className="p-4 text-center">
            <Clock className="w-8 h-8 text-yellow-600 mx-auto mb-2" />
            <p className="text-2xl font-bold text-yellow-700">{stats.pending}</p>
            <p className="text-sm text-yellow-600">In Asteptare</p>
          </CardContent>
        </Card>
        <Card 
          className={`card-industrial cursor-pointer ${filterStatus === 'approved' ? 'border-green-500' : ''}`}
          onClick={() => setFilterStatus('approved')}
        >
          <CardContent className="p-4 text-center">
            <CheckCircle className="w-8 h-8 text-green-600 mx-auto mb-2" />
            <p className="text-2xl font-bold text-green-700">{stats.approved}</p>
            <p className="text-sm text-green-600">Aprobate</p>
          </CardContent>
        </Card>
        <Card 
          className={`card-industrial cursor-pointer ${filterStatus === 'rejected' ? 'border-red-500' : ''}`}
          onClick={() => setFilterStatus('rejected')}
        >
          <CardContent className="p-4 text-center">
            <XCircle className="w-8 h-8 text-red-600 mx-auto mb-2" />
            <p className="text-2xl font-bold text-red-700">{stats.rejected}</p>
            <p className="text-sm text-red-600">Respinse</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <Input
            placeholder="Cauta cerere..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 h-12"
          />
        </div>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-full md:w-48 h-12">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toate</SelectItem>
            <SelectItem value="pending">In Asteptare</SelectItem>
            <SelectItem value="approved">Aprobate</SelectItem>
            <SelectItem value="rejected">Respinse</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Approvals List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0052CC]"></div>
        </div>
      ) : filtered.length > 0 ? (
        <div className="space-y-3">
          {filtered.map((approval) => (
            <Card key={approval.id} className="card-industrial" data-testid={`approval-card-${approval.id}`}>
              <CardContent className="p-4">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex items-start gap-3">
                    <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                      approval.status === 'pending' ? 'bg-yellow-100' :
                      approval.status === 'approved' ? 'bg-green-100' : 'bg-red-100'
                    }`}>
                      {approval.status === 'pending' ? (
                        <Clock className="w-6 h-6 text-yellow-600" />
                      ) : approval.status === 'approved' ? (
                        <CheckCircle className="w-6 h-6 text-green-600" />
                      ) : (
                        <XCircle className="w-6 h-6 text-red-600" />
                      )}
                    </div>
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-semibold text-slate-900">{getTypeLabel(approval.request_type)}</p>
                        {getStatusBadge(approval.status)}
                      </div>
                      <p className="text-sm text-slate-600 mt-1">{approval.description}</p>
                      <div className="flex items-center gap-4 mt-2 text-sm text-slate-500">
                        <span className="flex items-center gap-1">
                          <User className="w-4 h-4" />
                          {approval.requested_by_name}
                        </span>
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {new Date(approval.created_at).toLocaleDateString('ro-RO')}
                        </span>
                      </div>
                    </div>
                  </div>
                  {approval.status === 'pending' && isAdmin && (
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        onClick={() => handleDecision(approval.id, 'approved')}
                        className="bg-green-600 hover:bg-green-700"
                      >
                        <Check className="w-4 h-4 mr-1" />
                        APROBA
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDecision(approval.id, 'rejected')}
                        className="text-red-600 border-red-300 hover:bg-red-50"
                      >
                        <X className="w-4 h-4 mr-1" />
                        RESPINGE
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="card-industrial">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <FileText className="w-16 h-16 text-slate-300 mb-4" />
            <p className="text-slate-500 text-lg">Nu exista cereri de aprobare</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ApprovalsPage;
